import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, MessageSquare, Phone, Send, Sparkles, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const newSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const ContactPage = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', country: '', message: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // AI chat state
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm Shopperden's AI assistant. Ask me anything about listings, subscriptions, pricing, or how the platform works — I'll reply instantly." },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatScrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/contact/info`)
      .then((r) => setContactInfo(r.data))
      .catch(() => setContactInfo({
        emails: ['shopperden@yahoo.com', 'adminshopperden@gmail.com'],
        whatsapp: '00447950523316',
        whatsapp_link: 'https://wa.me/447950523316',
        hours: 'Mon–Fri, 9:00–18:00 UK time',
        response_sla: 'Within 1 working day',
      }));
  }, []);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, chatBusy]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error('Please provide your name, email, and a message of at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact/message`, form);
      setSubmitted(true);
      toast.success("Thanks — we'll get back to you shortly.");
      setForm({ name: '', email: '', phone: '', country: '', message: '', website: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const sendChat = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setChatInput('');
    setChatBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/contact/ai-chat`, { message: text, session_id: sessionId });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "I couldn't reply just now — please email shopperden@yahoo.com.";
      setMessages((m) => [...m, { role: 'assistant', text: msg }]);
    } finally {
      setChatBusy(false);
    }
  }, [chatInput, chatBusy, sessionId]);

  const onChatKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChat();
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="contact-page">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F766E] text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FED7AA]" />
              <p className="text-[11px] tracking-[0.28em] uppercase text-[#FED7AA] font-semibold">Get in touch</p>
            </div>

            <h1 className="font-['Playfair_Display'] font-semibold leading-[1.05] tracking-tight text-[2.25rem] sm:text-5xl lg:text-[4rem]">
              <span className="block">We'd love to</span>
              <span className="block text-[#FED7AA] italic">hear from you</span>
            </h1>

            <p className="mt-6 text-white/75 text-[15px] sm:text-lg leading-relaxed max-w-2xl">
              Questions about listings, subscriptions, partnerships or a specific property?
              Email us, WhatsApp us, or ask our AI assistant — whichever's fastest for you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a
            href={`mailto:${contactInfo?.emails?.[0] || 'shopperden@yahoo.com'}`}
            className="group bg-white border border-[#E2E8F0] p-7 hover:border-[#E07A3A] transition-colors"
            data-testid="contact-email-primary"
          >
            <Mail className="h-6 w-6 text-[#E07A3A] mb-4" />
            <p className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-2">Primary email</p>
            <p className="font-['Playfair_Display'] text-xl text-[#0F172A] group-hover:text-[#E07A3A] transition-colors break-all">
              {contactInfo?.emails?.[0] || 'shopperden@yahoo.com'}
            </p>
          </a>
          <a
            href={`mailto:${contactInfo?.emails?.[1] || 'adminshopperden@gmail.com'}`}
            className="group bg-white border border-[#E2E8F0] p-7 hover:border-[#E07A3A] transition-colors"
            data-testid="contact-email-admin"
          >
            <Mail className="h-6 w-6 text-[#E07A3A] mb-4" />
            <p className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-2">Admin / Partnerships</p>
            <p className="font-['Playfair_Display'] text-xl text-[#0F172A] group-hover:text-[#E07A3A] transition-colors break-all">
              {contactInfo?.emails?.[1] || 'adminshopperden@gmail.com'}
            </p>
          </a>
          <a
            href={contactInfo?.whatsapp_link || 'https://wa.me/447950523316'}
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-[#0F766E] text-white p-7 hover:bg-[#0F766E]/90 transition-colors"
            data-testid="contact-whatsapp"
          >
            <Phone className="h-6 w-6 mb-4" />
            <p className="text-xs tracking-[0.2em] uppercase text-white/70 mb-2">WhatsApp (fastest)</p>
            <p className="font-['Playfair_Display'] text-xl">+44 7950 523316</p>
            <p className="text-sm text-white/70 mt-2 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {contactInfo?.hours || 'Mon–Fri, 9:00–18:00 UK'}
            </p>
          </a>
        </div>
      </section>

      {/* Form + AI chat */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Contact form */}
          <div className="lg:col-span-3 bg-white border border-[#E2E8F0] p-7 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <MessageSquare className="h-5 w-5 text-[#E07A3A]" />
              <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A]">Leave us a message</h2>
            </div>
            <p className="text-sm text-[#64748B] mb-7">
              We reply within one working day. For urgent matters, use WhatsApp.
            </p>

            {submitted ? (
              <div className="border border-[#0F766E]/30 bg-[#0F766E]/5 p-8 text-center" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full bg-[#0F766E]/15 flex items-center justify-center mx-auto mb-4">
                  <Send className="h-5 w-5 text-[#0F766E]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] mb-2">Message received</h3>
                <p className="text-sm text-[#64748B] mb-5">A confirmation email is on its way. We'll reply within one working day.</p>
                <Button variant="outline" onClick={() => setSubmitted(false)} data-testid="contact-send-another">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                {/* Honeypot — hidden from humans, bots fill it */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={setField('website')}
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-1.5 block">Name *</label>
                    <Input
                      required
                      value={form.name}
                      onChange={setField('name')}
                      placeholder="Your full name"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-1.5 block">Email *</label>
                    <Input
                      required
                      type="email"
                      value={form.email}
                      onChange={setField('email')}
                      placeholder="you@example.com"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-1.5 block">Phone (optional)</label>
                    <Input
                      value={form.phone}
                      onChange={setField('phone')}
                      placeholder="+44 …"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-1.5 block">Country (optional)</label>
                    <Input
                      value={form.country}
                      onChange={setField('country')}
                      placeholder="UK / India / Canada"
                      data-testid="contact-input-country"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#64748B] mb-1.5 block">Message *</label>
                  <Textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={setField('message')}
                    placeholder="How can we help?"
                    minLength={10}
                    maxLength={5000}
                    data-testid="contact-input-message"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1.5">{form.message.length}/5000</p>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-[#94A3B8]">Protected by anti-spam filters.</p>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#0F172A] hover:bg-[#0F172A]/90 text-white"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="h-4 w-4 mr-2" /> Send Message</>)}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* AI assistant */}
          <div className="lg:col-span-2 bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white flex flex-col border border-[#0F172A]">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#10B981] to-[#0F766E] flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-['Playfair_Display'] text-lg leading-tight">Shopperden AI</p>
                  <p className="text-xs text-white/50">Instant answers · 24/7</p>
                </div>
              </div>
            </div>

            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 max-h-[420px] min-h-[360px]" data-testid="contact-chat-window">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                      m.role === 'user'
                        ? 'bg-[#E07A3A] text-white rounded-br-sm'
                        : 'bg-white/10 text-white/90 rounded-bl-sm'
                    }`}
                    data-testid={`chat-msg-${m.role}-${i}`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {chatBusy && (
                <div className="flex justify-start">
                  <div className="bg-white/10 text-white/70 px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={onChatKey}
                  placeholder="Ask Shopperden AI…"
                  maxLength={1500}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="contact-chat-input"
                />
                <Button
                  onClick={sendChat}
                  disabled={chatBusy || !chatInput.trim()}
                  className="bg-[#E07A3A] hover:bg-[#E07A3A]/90 text-white flex-shrink-0"
                  data-testid="contact-chat-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[10px] text-white/40 mt-2 tracking-wider uppercase">
                AI answers are informational. For legal / billing issues, use the form.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
